/**
 * /sites/:siteId/analytics — the "double down" board.
 *
 * One thing: a grid of tiles, one per article currently on page 1. Each tile
 * shows the headline query, the metrics (impressions / clicks / CTR / pos),
 * and the query cluster the article ranks for — everything you need to rewrite
 * it and own the cluster. Click a tile to open that article in the editor.
 *
 * The board is computed server-side by the same weekly-digest code that powers
 * the email, so the screen and the inbox never drift.
 *
 * Below the board: an additive classification section (PROTECT/OPTIMIZE-CTR/
 * EDIT/CONSOLIDATE/PRUNE/UNMATCHED) for existing pages — a complementary
 * signal, not a replacement. The page-1 board says "these are working, double
 * down"; classification says "here's what to do with everything else."
 */

import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { api } from '../lib/api.ts'
import { friendlyError } from '../lib/friendly-errors.ts'

interface QueryStat {
  query: string
  position: number
  impressions: number
  clicks: number
}

interface WinnerFlag {
  page: string
  slug: string
  title: string
  query: string
  position: number
  impressions: number
  clicks: number
  ctr: number
  otherPageOneQueries: number
  topQueries: QueryStat[]
}

// 5-class system (PROTECT/OPTIMIZE-CTR/EDIT/CONSOLIDATE/PRUNE/UNMATCHED) —
// additive alongside the page-1 board above. Mirrors seo-classifier.ts.
// Recommendations only — PRUNE/CONSOLIDATE require human approval before
// anything executes; nothing here auto-runs.
type PageClass = 'PROTECT' | 'OPTIMIZE-CTR' | 'EDIT' | 'CONSOLIDATE' | 'PRUNE' | 'UNMATCHED'
interface ClassifiedPage {
  page: string
  role: 'article' | 'structural' | 'money' | 'unmatched'
  ageDays: number
  impressions90d: number
  clicks90d: number
  position90d: number | null
  class: PageClass
  reason: string
  pruneRank?: number
  pruneStatus?: 'actionable' | 'queued'
}
interface ClassificationSummary {
  version: string
  generatedAt: number
  distribution: Record<PageClass, number>
  pages: ClassifiedPage[]
}

interface AnalyticsResponse {
  siteId: string
  lastRefreshAt: number | null
  lastRefreshTrigger: string | null
  rowsInSnapshot: number
  hasSnapshot: boolean
  onPageOne: WinnerFlag[]
  classification: ClassificationSummary | null
}

// ---- Funnel Monitor (weekly, GET /api/sites/:siteId/analytics/funnel) ----
// Mirrors lib/funnel-monitor.ts FunnelSnapshotPayload on the API side.
interface BucketSummary {
  pages: number
  impressions: number
  clicks: number
}
interface FunnelActionRow {
  page: string
  impressions: number
  clicks: number
  position: number
}
interface FunnelLaggardRow extends FunnelActionRow {
  ctr: number
}
interface CaptureStats {
  totalCaptureEvents: number
  pagesWithCapture: number
  pagesClickedNeverCaptured: number
}
interface FunnelSnapshot {
  weekStart: string
  createdAt: number
  pagesWithImpressions: number
  page1: BucketSummary
  striking: BucketSummary
  nowhere: BucketSummary
  pageOneCtr: number
  topStriking: FunnelActionRow[]
  ctrLaggards: FunnelLaggardRow[]
  capture: CaptureStats | null
}
interface FunnelResponse {
  siteId: string
  hasSnapshot: boolean
  current: FunnelSnapshot | null
  previous: FunnelSnapshot | null
}

export function AnalyticsScreen() {
  const { siteId = 'chc' } = useParams()
  const [data, setData] = useState<AnalyticsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null)
  const [funnel, setFunnel] = useState<FunnelResponse | null>(null)

  function load() {
    setLoading(true)
    api<AnalyticsResponse>(`/api/sites/${siteId}/analytics`)
      .then((res) => setData(res))
      .catch((err) => setError(friendlyError(err).message))
      .finally(() => setLoading(false))
  }
  useEffect(load, [siteId])

  useEffect(() => {
    api<FunnelResponse>(`/api/sites/${siteId}/analytics/funnel`)
      .then((res) => setFunnel(res))
      .catch(() => setFunnel(null)) // funnel panel is additive — a failure here shouldn't block the page-1 board
  }, [siteId])

  async function refreshNow(force = false) {
    setRefreshing(true)
    setRefreshMsg(null)
    try {
      const res = await api<{
        ok: boolean
        rowsWritten: number
        skipped?: boolean
        reason?: string
        error?: string
      }>(`/api/sites/${siteId}/analytics/refresh${force ? '?force=1' : ''}`, { method: 'POST' })
      if (res.skipped) {
        setRefreshMsg(`Skipped: ${res.reason ?? 'recent refresh exists'}.`)
      } else if (res.error) {
        setRefreshMsg(`Refresh failed: ${res.error}`)
      } else {
        setRefreshMsg(`Refreshed — ${res.rowsWritten} rows from GSC.`)
        load()
      }
    } catch (err) {
      setRefreshMsg(`Refresh failed: ${(err as Error).message}`)
    } finally {
      setRefreshing(false)
    }
  }

  const items = data?.onPageOne ?? []

  return (
    <>
      <div className="page-header">
        <div>
          <h1>On page 1 — double down</h1>
          <div className="subtitle">
            {data?.hasSnapshot ? `${items.length} article${items.length === 1 ? '' : 's'} ranking on page 1` : 'Articles ranking on page 1'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>
            {data?.lastRefreshAt ? `Refreshed ${ago(data.lastRefreshAt)} (${data.lastRefreshTrigger})` : 'Never refreshed'}
          </div>
          <button type="button" className="btn" onClick={() => refreshNow(false)} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh now'}
          </button>
        </div>
      </div>

      {refreshMsg && <div style={{ marginBottom: '1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{refreshMsg}</div>}
      {error && <div className="error-state">{error}</div>}
      {loading && !data && <div className="loading">Loading…</div>}

      {data && !data.hasSnapshot && (
        <div style={emptyStyle}>
          No analytics snapshot yet. Click <strong>Refresh now</strong> to pull the last 28 days of GSC data.
        </div>
      )}

      {data?.hasSnapshot && items.length === 0 && (
        <div style={emptyStyle}>Nothing on page 1 yet. Keep publishing — winners show up here as Google ranks them.</div>
      )}

      {items.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.85rem' }}>
          {items.map((w, i) => (
            <Tile key={i} siteId={siteId} w={w} />
          ))}
        </div>
      )}

      {data?.classification && (
        <div style={{ marginTop: '1.5rem' }}>
          <ClassificationCard siteId={siteId} data={data.classification} />
        </div>
      )}

      {funnel?.hasSnapshot && funnel.current && (
        <div style={{ marginTop: '1.5rem' }}>
          <FunnelCard current={funnel.current} previous={funnel.previous} />
        </div>
      )}
    </>
  )
}

// ====================================================================
// Funnel Monitor — weekly position-bucket rollup (PAGE1/STRIKING/NOWHERE)
// + capture stats. Populated by the funnel_monitor_weekly cron stage
// (lib/daily-cron.ts / lib/funnel-monitor.ts on the API side). Same plain,
// self-contained styling as ClassificationCard above.
// ====================================================================

function FunnelCard({ current, previous }: { current: FunnelSnapshot; previous: FunnelSnapshot | null }) {
  const stages: Array<{ key: 'page1' | 'striking' | 'nowhere'; label: string }> = [
    { key: 'page1', label: 'PAGE 1' },
    { key: 'striking', label: 'STRIKING (11-20)' },
    { key: 'nowhere', label: 'NOWHERE (21+)' },
  ]

  return (
    <section style={{ borderLeft: '4px solid var(--accent)', paddingLeft: '0.85rem', display: 'grid', gap: '0.85rem' }}>
      <div>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.05em' }}>
          🔻 FUNNEL MONITOR
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-faint)', marginTop: '0.15rem' }}>
          Week of {current.weekStart} · {current.pagesWithImpressions.toLocaleString()} pages with impressions (28d) · page-1 CTR{' '}
          {(current.pageOneCtr * 100).toFixed(2)}%{previous ? ctrDelta(current.pageOneCtr, previous.pageOneCtr) : ''}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap' }}>
        {stages.map(({ key, label }) => (
          <StageTile key={key} label={label} curr={current[key]} prev={previous?.[key] ?? null} />
        ))}
      </div>

      {current.topStriking.length > 0 && (
        <div style={funnelPanelStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h3 style={{ fontSize: '0.95rem', margin: 0 }}>🎯 Striking distance — internal links + refresh</h3>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-faint)' }}>top {current.topStriking.length} by impressions</span>
          </div>
          <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.5rem' }}>
            {current.topStriking.map((r) => (
              <FunnelActionLine key={r.page} r={r} trailing={`pos ${r.position} · ${r.impressions.toLocaleString()} impr`} />
            ))}
          </div>
        </div>
      )}

      {current.ctrLaggards.length > 0 && (
        <div style={funnelPanelStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h3 style={{ fontSize: '0.95rem', margin: 0 }}>✂️ Page-1 CTR laggards — title/snippet rewrite or AIO check</h3>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-faint)' }}>top {current.ctrLaggards.length} by impressions</span>
          </div>
          <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.5rem' }}>
            {current.ctrLaggards.map((r) => (
              <FunnelActionLine
                key={r.page}
                r={r}
                trailing={`pos ${r.position} · ${(r.ctr * 100).toFixed(2)}% CTR · ${r.impressions.toLocaleString()} impr`}
              />
            ))}
          </div>
        </div>
      )}

      {current.capture && (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          <strong>{current.capture.totalCaptureEvents.toLocaleString()}</strong> capture events (28d) on{' '}
          <strong>{current.capture.pagesWithCapture}</strong> page{current.capture.pagesWithCapture === 1 ? '' : 's'}
          {current.capture.pagesClickedNeverCaptured > 0 && (
            <>
              {' '}
              · <strong>{current.capture.pagesClickedNeverCaptured}</strong> page
              {current.capture.pagesClickedNeverCaptured === 1 ? '' : 's'} got clicks but never captured
            </>
          )}
        </div>
      )}
    </section>
  )
}

function StageTile({ label, curr, prev }: { label: string; curr: BucketSummary; prev: BucketSummary | null }) {
  return (
    <div style={{ ...funnelPanelStyle, minWidth: '150px', flex: '1 1 150px' }}>
      <div style={{ fontSize: '0.68rem', color: 'var(--text-faint)', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>
        {curr.pages.toLocaleString()} <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--text-faint)' }}>pages</span>
        {prev && countDelta(curr.pages, prev.pages)}
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
        {curr.impressions.toLocaleString()} impr · {curr.clicks.toLocaleString()} clicks
      </div>
    </div>
  )
}

function FunnelActionLine({ r, trailing }: { r: FunnelActionRow; trailing: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.8rem' }}>
      <span title={r.page} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
        {pathFromUrl(r.page)}
      </span>
      <span style={{ whiteSpace: 'nowrap', fontSize: '0.72rem', color: 'var(--text-faint)' }}>{trailing}</span>
    </div>
  )
}

function pathFromUrl(url: string): string {
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}

function countDelta(curr: number, prev: number) {
  const d = curr - prev
  if (d === 0) return null
  const color = d > 0 ? 'var(--ok)' : 'var(--danger)'
  return (
    <span style={{ fontSize: '0.7rem', fontWeight: 700, color, marginLeft: '0.35rem' }}>
      {d > 0 ? '+' : ''}
      {d}
    </span>
  )
}

function ctrDelta(curr: number, prev: number): string {
  const d = (curr - prev) * 100
  if (Math.abs(d) < 0.01) return ''
  return ` (${d > 0 ? '+' : ''}${d.toFixed(2)}pp WoW)`
}

const funnelPanelStyle = {
  background: 'var(--bg-elev)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  padding: '0.85rem 1.1rem',
} as const

function Tile({ siteId, w }: { siteId: string; w: WinnerFlag }) {
  return (
    <Link to={`/sites/${siteId}/articles/${w.slug}`} title={w.page} style={tileStyle}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3, marginBottom: '0.3rem' }}>{w.title}</div>

      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>
        People search: <strong>{w.query}</strong>
      </div>

      <div style={{ display: 'flex', gap: '0.7rem', fontSize: '0.74rem', flexWrap: 'wrap' }}>
        <span>
          Rank <strong style={{ color: 'var(--accent)' }}>#{Math.round(w.position)}</strong>
        </span>
        <span>
          Shown <strong>{w.impressions.toLocaleString()}×</strong>
        </span>
        <span>
          <strong>{w.clicks.toLocaleString()}</strong> click{w.clicks === 1 ? '' : 's'}
        </span>
      </div>

      {w.topQueries.length > 1 && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
          <div style={{ fontSize: '0.66rem', color: 'var(--text-faint)', marginBottom: '0.3rem' }}>Also shows up for:</div>
          {w.topQueries.slice(0, 5).map((q, j) => (
            <div key={j} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.7rem', padding: '0.1rem 0' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.query}</span>
              <span style={{ color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
                Rank #{Math.round(q.position)} · shown {q.impressions.toLocaleString()}×
              </span>
            </div>
          ))}
        </div>
      )}
    </Link>
  )
}

// ====================================================================
// 5-class classification (new, additive) — distribution + the PRUNE
// candidates due for review this cycle (the 10%-cap "actionable" set).
// Self-contained styling (this screen has no Panel/list helpers — kept
// deliberately minimal to match the page-1 board's plain aesthetic).
// ====================================================================

function ClassificationCard({ siteId, data }: { siteId: string; data: ClassificationSummary }) {
  const actionablePrune = data.pages
    .filter((p) => p.class === 'PRUNE' && p.pruneStatus === 'actionable')
    .sort((a, b) => (a.pruneRank ?? 0) - (b.pruneRank ?? 0))
  const queuedPruneCount = data.pages.filter((p) => p.class === 'PRUNE' && p.pruneStatus === 'queued').length
  const unmatched = data.pages.filter((p) => p.class === 'UNMATCHED')

  const order: PageClass[] = ['PROTECT', 'OPTIMIZE-CTR', 'EDIT', 'CONSOLIDATE', 'PRUNE', 'UNMATCHED']

  return (
    <section style={{ borderLeft: '4px solid #8b5cf6', paddingLeft: '0.85rem', display: 'grid', gap: '0.85rem' }}>
      <div>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#8b5cf6', letterSpacing: '0.05em' }}>🧭 CLASSIFICATION</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-faint)', marginTop: '0.15rem' }}>
          PROTECT / OPTIMIZE-CTR / EDIT / CONSOLIDATE / PRUNE — recommendations only. PRUNE and CONSOLIDATE require your
          approval here before anything happens; nothing auto-executes.
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
        {order.map((cls) => (
          <div key={cls} style={{ fontSize: '0.85rem' }}>
            <strong>{data.distribution[cls]}</strong> <span style={{ color: 'var(--text-faint)' }}>{cls}</span>
          </div>
        ))}
      </div>

      {actionablePrune.length > 0 && (
        <div style={classificationPanelStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h3 style={{ fontSize: '0.95rem', margin: 0 }}>🗑️ PRUNE — due this cycle</h3>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-faint)' }}>{actionablePrune.length} candidates</span>
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-faint)', margin: '0.2rem 0 0.6rem' }}>
            Ranked worst-first (0 impressions, oldest first); capped to 10% of article pages per cycle
            {queuedPruneCount > 0 ? ` — ${queuedPruneCount} more queued for next cycle` : ''}.
          </div>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {actionablePrune.map((p) => (
              <div key={p.page} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.8rem' }}>
                <div style={{ minWidth: 0 }}>
                  <ClassPageLink page={p.page} />
                  <div style={{ color: 'var(--text-faint)', fontSize: '0.7rem' }}>{p.reason}</div>
                </div>
                <div style={{ whiteSpace: 'nowrap', fontSize: '0.72rem', color: 'var(--text-faint)' }}>
                  {p.impressions90d} impr · {p.clicks90d} clicks
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {unmatched.length > 0 && (
        <div style={classificationPanelStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h3 style={{ fontSize: '0.95rem', margin: 0 }}>❓ UNMATCHED</h3>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-faint)' }}>{unmatched.length} candidates</span>
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-faint)', margin: '0.2rem 0 0.6rem' }}>
            GSC reports these but they match no known route — may be redirects, aliases, or retired pages. Needs a live
            check; never auto-pruned.
          </div>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {unmatched.map((p) => (
              <div key={p.page} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.8rem' }}>
                <ClassPageLink page={p.page} />
                <div style={{ whiteSpace: 'nowrap', fontSize: '0.72rem', color: 'var(--text-faint)' }}>
                  {p.impressions90d} impr · pos {p.position90d?.toFixed(1) ?? 'n/a'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

/** Classification pages are normalized paths (e.g. "/blog/foo"), not full
 *  URLs — many are legacy hand-coded pages or blog posts outside the JSON
 *  article registry, so there's no guaranteed editor route (unlike the
 *  page-1 board's Tile, which always has a registry slug). Plain text
 *  rather than a link that might not go anywhere real. */
function ClassPageLink({ page }: { page: string }) {
  return <span title={page}>{page}</span>
}

const classificationPanelStyle = {
  background: 'var(--bg-elev)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  padding: '0.85rem 1.1rem',
} as const

const tileStyle = {
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
  background: 'var(--bg-elev)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  padding: '0.85rem 1rem',
} as const

const emptyStyle = {
  border: '1px dashed var(--border-strong)',
  borderRadius: 'var(--radius-lg)',
  padding: '1.5rem',
  background: 'var(--bg-muted)',
  color: 'var(--text-muted)',
  textAlign: 'center',
} as const

function ago(unixSeconds: number): string {
  const diffH = (Date.now() / 1000 - unixSeconds) / 3600
  if (diffH < 1) return `${Math.max(1, Math.floor(diffH * 60))}m ago`
  if (diffH < 24) return `${Math.floor(diffH)}h ago`
  return `${Math.floor(diffH / 24)}d ago`
}
